import { useQueryClient } from "@tanstack/react-query";
import { Avatar, Divider, Dropdown } from "antd";
import { ItemType, MenuItemType } from "antd/es/menu/interface";
import { Location } from "history";
import { MenuInfo } from "rc-menu/lib/interface";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import ActionTextIcon from "../../../../common/components/icons/ActionTextIcon";
import AntIcon from "../../../../common/components/icons/AntIcon";
import { Permission } from "../../../../common/security/authorization/enums";
import { RootState } from "../../../../common/types";
import { createMenuItem } from "../../../../common/utils/menuUtils";
import { hasPermission } from "../../../../common/utils/utils";
import AgentProfilePictureView from "../../../../modules/agent/components/views/profilepicture/AgentProfilePictureView";
import { formatAgentAggregatedName } from "../../../../modules/agent/utils";
import {
  hasUserMultipleAccounts,
  logoutAction,
  selectUser,
  selectUserAccount,
  switchSelectedAccountAction
} from "../../../../modules/auth/ducks";
import { selectRouterLocation } from "../../../../modules/ducks";
import { CURRENT_USER_ROUTE_PATHS } from "../../../../modules/user/paths";
import { User, UserAccount } from "../../../../modules/user/types";
import styles from "./HeaderUserMenuView.module.scss";

const MENU_KEY = {
  LOGOUT: "logout",
  PROFILE: "profile",
  COMMISSIONS: "commissions"
};

export const HeaderUserMenuView = () => {
  const { t } = useTranslation();

  const user = useSelector<RootState, User | undefined>(selectUser);
  const userAccount = useSelector<RootState, UserAccount | undefined>(selectUserAccount);
  const hasMultipleAccounts = useSelector<RootState, boolean | undefined>(hasUserMultipleAccounts);
  const routerLocation = useSelector<RootState, Location>(selectRouterLocation);

  const queryClient = useQueryClient();

  const dispatch = useDispatch();
  const actions = useMemo(
    () =>
      bindActionCreators(
        {
          onLogout: logoutAction,
          onSelectedAccountSwitch: switchSelectedAccountAction
        },
        dispatch
      ),
    [dispatch]
  );

  const handleMenuClick = (event: MenuInfo) => {
    const { key } = event;

    switch (key) {
      case MENU_KEY.LOGOUT:
        actions.onLogout();
        queryClient.invalidateQueries();
        break;
      case MENU_KEY.PROFILE:
      case MENU_KEY.COMMISSIONS:
        break;
      default:
        if (key !== userAccount?.id) {
          actions.onSelectedAccountSwitch(key);
          queryClient.invalidateQueries();
        }
        break;
    }
  };

  const getUserMenuItems = (userAccount: UserAccount): ItemType[] => {
    const userMenuItems: ItemType[] = [];

    if (!userAccount?.agent) {
      return [];
    }

    const menuItem = createMenuItem({
      path: CURRENT_USER_ROUTE_PATHS.profile.to,
      key: MENU_KEY.PROFILE,
      label: t("navigation.header.user.profile"),
      icon: <AntIcon type="user" />,
      permissions: userAccount?.permissions,
      routerLocation
    });

    if (menuItem) {
      userMenuItems.push(menuItem);
    }

    if (hasPermission(userAccount.permissions, Permission.PROFILE_COMMISSIONS)) {
      const menuItem = createMenuItem({
        path: CURRENT_USER_ROUTE_PATHS.commissions.to,
        key: MENU_KEY.COMMISSIONS,
        label: t("navigation.header.user.commissions"),
        icon: <AntIcon type="euro" />,
        permissions: userAccount.permissions,
        routerLocation
      });

      if (menuItem) {
        userMenuItems.push(menuItem);
      }
    }

    userMenuItems.push({ type: "divider", key: "user-dropdown-divider-1" });

    if (hasMultipleAccounts) {
      let path: string | undefined;

      const orderedAccounts = userAccount?.agent
        ? user?.agentUserRole?.userAccounts
            ?.filter(account => account.confirmed && !account.disabled && account.agent)
            .sort((a, b) => {
              const accountATreePath = a.agent?.accessTreePath ?? "";
              const accountBTreePath = b.agent?.accessTreePath ?? "";

              return accountATreePath.localeCompare(accountBTreePath);
            })
            .reduce<MenuItemType[]>((accumulator, account, index) => {
              if (index === 0) {
                path = account.agent?.accessTreePath;
              } else if (!account.agent?.accessTreePath?.startsWith(path ?? "")) {
                path = account.agent?.accessTreePath;

                accumulator.push({
                  key: `${account.id}-divider`,
                  label: <Divider style={{ margin: 0 }} />
                });
              }

              accumulator.push({
                key: account.id,
                label: account.representingAgent ? (
                  <div style={{ display: "block" }}>
                    <div>{formatAgentAggregatedName(account.agent)}</div>
                    <div className={styles.representingAgentName}>
                      {formatAgentAggregatedName(account.representingAgent)}
                    </div>
                  </div>
                ) : (
                  formatAgentAggregatedName(account.agent)
                )
              });

              return accumulator;
            }, [])
        : user?.clientUserRole?.userAccounts
            ?.filter(account => account.confirmed && !account.disabled)
            .map<MenuItemType>(account => ({ key: account.id, label: account.clients?.[0]?.aggregatedName }));

      userMenuItems.push(
        {
          type: "group",
          key: "user-dropdown-group-accounts",
          label: (
            <ActionTextIcon icon="user-switch" text={t("navigation.header.user.accounts")} className={styles.text} />
          ),
          children: orderedAccounts
        },
        { type: "divider", key: "user-dropdown-divider-2" }
      );
    }

    userMenuItems.push({ key: MENU_KEY.LOGOUT, label: t("navigation.header.logout"), icon: <AntIcon type="logout" /> });

    return userMenuItems;
  };

  return (
    <Dropdown
      trigger={["click"]}
      placement="bottomRight"
      overlayStyle={{ height: window.innerHeight - 60, overflow: "auto" }}
      menu={{
        style: { maxHeight: window.innerHeight - 60 },
        items: userAccount ? getUserMenuItems(userAccount) : [],
        selectedKeys: userAccount?.id ? [userAccount.id] : [],
        onClick: handleMenuClick
      }}
    >
      <div className={styles.action}>
        {userAccount?.agent?.profilePicture ? (
          <AgentProfilePictureView
            className={styles.accountProfilePicture}
            agent={userAccount.agent}
            width={38}
            preview={false}
            showActions={false}
          />
        ) : (
          <Avatar className={styles.accountAvatar} size="small" icon={<AntIcon type="user" />} />
        )}

        <div>
          <span className={styles.accountName}>{user?.name}</span>
          <span className={styles.accountName}>
            {formatAgentAggregatedName(userAccount?.agent) || userAccount?.clients?.[0]?.aggregatedName}
          </span>
        </div>
      </div>
    </Dropdown>
  );
};
